import React, { useContext, useEffect, useState, ChangeEvent, FC } from 'react';

import AddIcon from '@material-ui/icons/Add';

import { GlobalContext } from '../../../store';
import { ITeam } from '../../../store/admin/eventTeams/store';
import { IAdminEvent } from '../../../store/admin/events/store';
import { IAdminTeamGame } from '../../../store/admin/teamGames/store';

import TooltipsTexts from '../../Tooltip/Utils/TooltipsTexts';
import { LightTooltip } from '../LightTooltip';
import { TeamCardMetadataStyled } from './TeamCardMetadata.Styles';
import { TeamCardActions } from '../TeamCardActions/TeamCardActions';
import { useRouteInfoState } from '../../RouteInfo/Context/RouteInfo.context';
import { ChangeTeamFlight } from '../ChangeTeamFlight';
import {
  useTeamsCreatorState,
  useTeamsCreatorDispatch
} from '../../../scenes/admin/EventWizard/TeamsCreator/Context/TeamsCreator.context';
import Input from '../../Input';
import { TTeamCardContext } from '../TeamCard';
import {
  useTeeSheetCreatorState,
  useTeeSheetCreatorDispatch
} from '../../../scenes/admin/EventWizard/TeeSheetCreator/Context/TeeSheetCreator.context';
import { useAdminRound } from '../../../contrib/Hooks/useAdminRound.hook';
import { AddIconButton } from '../AddIconButton.Styles';

interface IProps {
  team: ITeam;
  hideActions?: boolean;
  displayChangeTeamFlight?: boolean;
  teamGame?: IAdminTeamGame;
  context: TTeamCardContext;
}

export const TeamCardMetadata: FC<IProps> = props => {
  const { team, hideActions, displayChangeTeamFlight, context, teamGame } = props;

  const {
    actions: {
      adminTeamGames: { create: createTeamGame },
      adminEventRounds: { get: getRound }
    },
    adminTeamGames,
    adminEventTeams,
    adminEvents
  } = useContext(GlobalContext);

  const { addTeamActive, groupIdToAdd } = useTeeSheetCreatorState();
  const { dispatch: dispatchTeeSheetCreator } = useTeeSheetCreatorDispatch();

  const { editMode, courseHandicap, selectedTeam } = useTeamsCreatorState();
  const { dispatch } = useTeamsCreatorDispatch();

  const { eventId, roundId, clubId } = useRouteInfoState();

  const [round] = useAdminRound(roundId);

  const [event, setEvent] = useState<IAdminEvent>();
  const [allowScramble, setAllowScramble] = useState(false);

  const [teamIsSelected, setTeamIsSelected] = useState(false);

  const [teamIndex, setTeamIndex] = useState(0);
  const [flightName, setFlightName] = useState('TBD');

  useEffect(() => {
    setTeamIsSelected(!!selectedTeam && selectedTeam.id === team.id);
  }, [selectedTeam, team]);

  useEffect(() => {
    const adminEvent = adminEvents[Number(eventId)];
    if (adminEvent) {
      setEvent(adminEvent);
      setAllowScramble(adminEvent.allowScramble);
    }
  }, [adminEvents, eventId]);

  useEffect(() => {
    if (!!event && !!team.flight) {
      const selectedFlight = event.flights.find(flight => flight.id === team.flight);

      if (selectedFlight) {
        setFlightName(selectedFlight.name);
      }
    }
  }, [event, team]);

  useEffect(() => {
    if (event && team && team.courseHandicap) {
      dispatch({ type: 'setCourseHandicap', value: team.courseHandicap });
    }
  }, [event, team]);

  useEffect(() => {
    if (round && round.groups) {
      const games = round.groups.flatMap(group => group.games);

      const teamGames = games.filter(roundGame => team.enrollments.includes(roundGame.enrollment));

      if (teamGames) {
        setTeamIndex(
          teamGames.reduce((acc, game) => Number(game.index) + acc, 0) / teamGames.length
        );
      }
    }
  }, [round, team]);

  const onCourseHandicapChange = (changeEvent: ChangeEvent<HTMLInputElement>) => {
    if (Number(changeEvent.target.value) > 0) {
      dispatch({ type: 'setCourseHandicap', value: Number(changeEvent.target.value) });
    }
  };

  useEffect(() => {
    if (teamIsSelected) {
      dispatch({ type: 'setCourseHandicap', value: team.courseHandicap || 0 });
    }
  }, [teamIsSelected, team]);

  const [teamIsSelectable, setTeamIsSelectable] = useState(true);

  const onSelectGroupToAdd = async () => {
    await createTeamGame(clubId, eventId, roundId, {
      group: groupIdToAdd,
      team: team.id
    });

    dispatchTeeSheetCreator({ type: 'set', value: { attribute: 'addTeamActive', value: false } });
    await getRound(Number(clubId), Number(eventId), Number(roundId), true);
  };

  useEffect(() => {
    if (adminTeamGames[groupIdToAdd]) {
      if (groupIdToAdd && round) {
        const groupTeamsIds = adminTeamGames[groupIdToAdd].map(game => game.team);

        const foundTeams = adminEventTeams[Number(eventId)]
          ? adminEventTeams[Number(eventId)].filter(eventTeam =>
              groupTeamsIds.includes(eventTeam.id)
            )
          : [];

        const groupPlayers = foundTeams.reduce(
          (playersAcc, foundTeam) =>
            foundTeam.enrollments ? playersAcc + foundTeam.enrollments.length : playersAcc,
          0
        );

        const remainingPlayers = round.playersPerTee - groupPlayers;

        setTeamIsSelectable(team.enrollments.length <= remainingPlayers);
      }
    } else {
      setTeamIsSelectable(true);
    }
  }, [adminTeamGames, groupIdToAdd, round, eventId]);

  return (
    <TeamCardMetadataStyled>
      <div className="team-card-header">
        {!team.isValid && (
          <LightTooltip title={TooltipsTexts['ROUND_TEAMS.INVALID_TEAM']} placement="top-start">
            <div className="team-warning" />
          </LightTooltip>
        )}

        <TeamCardActions
          team={team}
          context={context}
          teamGame={teamGame}
          hideActions={hideActions}
        />

        {addTeamActive && context === 'teeListSelector' && teamIsSelectable && (
          <AddIconButton onClick={onSelectGroupToAdd}>
            <AddIcon />
          </AddIconButton>
        )}
      </div>

      <p className="metadata">
        <span className="title">TEAM {allowScramble ? 'HDCP' : 'INDEX'}</span>

        {editMode && allowScramble && teamIsSelected ? (
          <Input
            type="number"
            value={courseHandicap || 0}
            onChange={onCourseHandicapChange}
            fullWidth={false}
            noIcon={true}
          />
        ) : (
          <span className="value">
            {allowScramble ? team.courseHandicap || 0 : teamIndex > 0 ? teamIndex.toFixed(1) : 0}
          </span>
        )}
      </p>

      <p className="metadata">
        <span className="title">FLIGHT</span>

        <span className="value">{flightName}</span>

        {displayChangeTeamFlight && <ChangeTeamFlight team={team} />}
      </p>
    </TeamCardMetadataStyled>
  );
};
